import React from 'react';

interface PaginationProps {
  total: number;
  page: number;         // 1-based
  pageSize: number;
  onPage: (p: number) => void;
  onPageSize?: (ps: number) => void;
  pageSizeOptions?: number[];
  style?: React.CSSProperties;
}

export default function Pagination({
  total, page, pageSize, onPage, onPageSize,
  pageSizeOptions = [20, 50, 100],
  style,
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = Math.min((page - 1) * pageSize + 1, total);
  const to   = Math.min(page * pageSize, total);

  // Build page window: always show first, last, and ±2 around current
  const pages: (number | '…')[] = [];
  const add = (n: number) => { if (!pages.includes(n)) pages.push(n); };
  add(1);
  for (let i = Math.max(2, page - 2); i <= Math.min(totalPages - 1, page + 2); i++) add(i);
  if (totalPages > 1) add(totalPages);
  // Insert ellipses
  const result: (number | '…')[] = [];
  let prev = 0;
  for (const p of pages as number[]) {
    if (p - prev > 1) result.push('…');
    result.push(p);
    prev = p;
  }

  const btn = (label: React.ReactNode, target: number | null, disabled = false, active = false): React.ReactNode => (
    <button
      key={String(label)}
      onClick={() => target !== null && onPage(target)}
      disabled={disabled}
      style={{
        minWidth: '30px', height: '30px', padding: '0 8px',
        borderRadius: 0, border: active ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.08)',
        background: active ? '#1e3a5f' : 'rgba(255,255,255,0.03)',
        color: active ? '#93c5fd' : disabled ? '#222222' : '#94a3b8',
        fontSize: '10px', fontWeight: active ? 700 : 400,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.12s',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', ...style }}>
      <span style={{ fontSize: '10px', color: '#555555', marginRight: '4px' }}>
        {total === 0 ? '0 results' : `${from}–${to} of ${total}`}
      </span>

      {btn('←', page > 1 ? page - 1 : null, page <= 1)}
      {result.map((r, i) =>
        r === '…'
          ? <span key={`el${i}`} style={{ color: '#222222', padding: '0 4px', fontSize: '10px' }}>…</span>
          : btn(r, r as number, false, r === page)
      )}
      {btn('→', page < totalPages ? page + 1 : null, page >= totalPages)}

      {onPageSize && (
        <select value={pageSize} onChange={e => onPageSize(Number(e.target.value))}
          style={{ padding: '4px 8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 0, color: '#94a3b8', fontSize: '10px', cursor: 'pointer', marginLeft: '4px' }}>
          {pageSizeOptions.map(s => <option key={s} value={s}>{s} / page</option>)}
        </select>
      )}
    </div>
  );
}

/** Hook to manage pagination state */
export function usePagination<T>(items: T[], defaultPageSize = 20) {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(defaultPageSize);

  // Reset to page 1 when items change
  React.useEffect(() => { setPage(1); }, [items.length]);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const paged = items.slice((page - 1) * pageSize, page * pageSize);

  const onPage = React.useCallback((p: number) => setPage(Math.max(1, Math.min(p, totalPages))), [totalPages]);
  const onPageSize = React.useCallback((ps: number) => { setPageSize(ps); setPage(1); }, []);

  return { page, pageSize, paged, total: items.length, onPage, onPageSize };
}
